import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Phone, 
  Mail, 
  MapPin, 
  User, 
  MessageSquare, 
  Send 
} from 'lucide-react';
import emailjs from '@emailjs/browser';

const ContactSection: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      // ⚠️ REMPLACEZ LES AUTRES ID PAR LES VÔTRES ⚠️
      await emailjs.send(
        'service_mborb09',       // Votre Service ID
        'template_ecuk56y',     // Template ID (à remplacer)
        {
          name: formData.name,
          email: formData.email,
          message: formData.message,
          time: new Date().toLocaleString('fr-FR', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        },
        'iAC-MOkRdX1x2emPt'      // Public Key (à remplacer)
      );
      
      setIsSent(true);
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setIsSent(false), 5000);
    } catch (err) {
      setError(t('contact.form.error'));
      console.error('Erreur envoi email:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section
     id="contact"
     className="relative py-36 bg-gradient-to-t from-white via-blue-50 to-white overflow-hidden">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="relative z-10 bg-white/95 backdrop-blur-sm rounded-xl p-8 shadow-xl">
          <h2 className="text-4xl font-extrabold text-center mb-10 bg-clip-text text-transparent bg-gradient-to-r from-[#4f837d] to-[#2e79b9] animate-fade-in">
            {t('contact.title')}
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Info */}
            <div className="bg-white/90 backdrop-blur-sm rounded-xl p-6 shadow-md border border-gray-100">
              <h3 className="font-semibold text-xl text-gray-800 mb-5">{t('contact.nous_contacter')}</h3>
              <ul className="space-y-4">
                <li className="flex items-start space-x-4">
                  <span className="mt-1 text-[#4f837d] text-xl"><Phone size={20} /></span>
                  <div>
                    <p className="text-sm font-medium text-gray-700">{t('contact.telephone')}</p>
                    <a 
                      href="tel:+22666390000" 
                      className="text-base text-gray-800 hover:text-[#2e79b9] transition-colors"
                    >
                      +226 66 39 00 00
                    </a>
                  </div>
                </li>
                <li className="flex items-start space-x-4">
                  <span className="mt-1 text-[#4f837d] text-xl"><Mail size={20} /></span>
                  <div>
                    <p className="text-sm font-medium text-gray-700">{t('contact.email')}</p>
                    <a 
                      href="mailto:Sales@takeoportunit.com" 
                      className="text-base text-gray-800 hover:text-[#2e79b9] transition-colors"
                    >
                      Sales@takeoportunit.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start space-x-4">
                  <span className="mt-1 text-[#4f837d] text-xl"><MapPin size={20} /></span>
                  <div>
                    <p className="text-sm font-medium text-gray-700">{t('contact.adresse')}</p>
                    <p className="text-base text-gray-800">Ouagadougou, Burkina Faso</p>
                  </div>
                </li>
              </ul>
            </div>
            
            {/* Contact Form */}
            <div className="bg-gray-50/80 backdrop-blur-sm rounded-xl p-6 shadow-md border border-gray-100">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <User className="absolute top-3 left-3 text-[#4f837d] transition-colors" size={18} />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t('contact.form.nom')}
                    className="w-full pl-10 pr-4 py-2.5 bg-white/70 border border-gray-200 rounded-lg focus:border-[#4f837d] focus:ring-2 focus:ring-[#4f837d]/20 outline-none transition-all text-base"
                    required
                  />
                </div>
                
                <div className="relative">
                  <Mail className="absolute top-3 left-3 text-[#4f837d] transition-colors" size={18} />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t('contact.form.email')}
                    className="w-full pl-10 pr-4 py-2.5 bg-white/70 border border-gray-200 rounded-lg focus:border-[#4f837d] focus:ring-2 focus:ring-[#4f837d]/20 outline-none transition-all text-base"
                    required
                  />
                </div>
                
                <div className="relative">
                  <MessageSquare className="absolute top-3 left-3 text-[#4f837d] transition-colors" size={18} />
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t('contact.form.message')}
                    rows={4}
                    className="w-full pl-10 pr-4 py-2.5 bg-white/70 border border-gray-200 rounded-lg focus:border-[#4f837d] focus:ring-2 focus:ring-[#4f837d]/20 outline-none transition-all text-base resize-none"
                    required
                  />
                </div>
                
                <div className="flex flex-col items-center gap-3">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`w-fit bg-gradient-to-r from-[#4f837d] to-[#2e79b9] text-white font-semibold py-2.5 px-5 rounded-lg hover:shadow-lg transition-all flex items-center space-x-2 ${
                      isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:scale-105'
                    }`}
                  >
                    {isLoading ? (
                      <div className="flex items-center">
                        <span className="loader mr-2"></span>
                        {t('contact.form.envoi')}
                      </div>
                    ) : (
                      <>
                        <Send size={18} />
                        <span>{t('contact.form.envoyer')}</span>
                      </>
                    )}
                  </button>
                  
                  {isSent && (
                    <div className="text-green-600 font-medium animate-fade-in">
                      {t('contact.form.success')}
                    </div>
                  )}
                  
                  {error && (
                    <div className="text-red-600 font-medium animate-fade-in">
                      {error}
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      
      <style >{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.5s ease-out; }
        
        .loader {
          border: 2px solid #f3f3f3;
          border-top: 2px solid #4f837d;
          border-radius: 50%;
          width: 16px;
          height: 16px;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </section>
  );
};

export default ContactSection;